import React, { useState, useEffect } from 'react';
import { check_enhance_coupon, submit_enhancement, get_enhancement_list, get_settings } from '../../../api/ControllApi';
import { toast } from 'react-toastify';
import { useUser } from '../../../context/LoginDetail';

// ─── Validation rules ─────────────────────────────────────────────────────────
const BANK_VALIDATORS = {
    bank_name: {
        label: 'Bank Name',
        validate: (v) => v.trim().length >= 2,
        message: 'Bank name must be at least 2 characters.',
    },
    bank_account_number: {
        label: 'Account Number',
        validate: (v) => /^\d{9,18}$/.test(v.trim()),
        message: 'Account number must be 9–18 digits.',
    },
    bank_ifsc_code: {
        label: 'IFSC Code',
        validate: (v) => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v.trim().toUpperCase()),
        message: 'Enter a valid IFSC code (e.g. HDFC0001234).',
    },
    bank_beneficiary_name: {
        label: 'Beneficiary Name',
        validate: (v) => /^[a-zA-Z\s]{2,}$/.test(v.trim()),
        message: 'Beneficiary name must contain only letters (min 2 chars).',
    },
    bank_phone_number: {
        label: 'Phone Number',
        validate: (v) => /^[6-9]\d{9}$/.test(v.trim()),
        message: 'Enter a valid 10-digit Indian mobile number.',
    },
};

const UPI_VALIDATORS = {
    gpay_upi_id: {
        label: 'UPI ID',
        validate: (v) => /^[\w.\-]{2,}@[\w]{2,}$/.test(v.trim()),
        message: 'Enter a valid UPI ID (e.g. name@upi).',
    },
    gpay_phone_number: {
        label: 'Phone Number',
        validate: (v) => /^[6-9]\d{9}$/.test(v.trim()),
        message: 'Enter a valid 10-digit Indian mobile number.',
    },
};

const INITIAL_FORM = {
    bank_name: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_beneficiary_name: '',
    bank_phone_number: '',
    gpay_upi_id: '',
    gpay_phone_number: '',
};

// ─── Helper: run validators and return error map ──────────────────────────────
function validate(paymentType, formData) {
    const validators = paymentType === 'bank' ? BANK_VALIDATORS : UPI_VALIDATORS;
    const errors = {};
    for (const [field, rule] of Object.entries(validators)) {
        const value = formData[field] || '';
        if (!value.trim()) {
            errors[field] = `${rule.label} is required.`;
        } else if (!rule.validate(value)) {
            errors[field] = rule.message;
        }
    }
    return errors;
}

// ─── FieldError helper component ─────────────────────────────────────────────
const FieldError = ({ msg }) =>
    msg ? <div className="invalid-feedback d-block mt-1">{msg}</div> : null;

// ─── Main component ───────────────────────────────────────────────────────────
function Enchashment() {
    const [couponCode, setCouponCode] = useState('');
    const [isValidated, setIsValidated] = useState(false);
    const [couponDetails, setCouponDetails] = useState(null);
    const [paymentType, setPaymentType] = useState('bank');
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [encashmentList, setEncashmentList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [encashmentNote, setEncashmentNote] = useState('');
    const { user } = useUser();

    useEffect(() => {
        if (user?.id) fetchEncashmentList(user.id);
        get_settings()
            .then(res => {
                if (res?.data?.encashment_description) setEncashmentNote(res.data.encashment_description);
            })
            .catch(err => {});
    }, [user?.id]);

    const fetchEncashmentList = async (userId) => {
        if (!userId) return;
        setListLoading(true);
        try {
            const res = await get_enhancement_list(userId);
            if (res.result || res.status) setEncashmentList(res.data);
        } catch (err) {

        } finally {
            setListLoading(false);
        }
    };

    const handleValidate = async () => {
        if (!couponCode.trim()) { toast.error("Please enter a coupon code"); return; }
        setLoading(true);
        try {
            const res = await check_enhance_coupon(couponCode.trim());
            if (res.result || res.status) {
                setIsValidated(true);
                setCouponDetails(res.data);
                toast.success(res.message || "Coupon validated successfully");
            } else {
                toast.error(res.message || "Invalid coupon code");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error validating coupon");
        } finally {
            setLoading(false);
        }
    };

    // Mark field as touched on blur and validate immediately
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const errors = validate(paymentType, { ...formData, [name]: e.target.value });
        setFieldErrors(prev => ({ ...prev, [name]: errors[name] }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Live-clear error once field becomes valid
        if (touched[name]) {
            const errors = validate(paymentType, { ...formData, [name]: value });
            setFieldErrors(prev => ({ ...prev, [name]: errors[name] }));
        }
    };

    // Switch payment type → reset errors and touched for the other tab
    const handlePaymentTypeChange = (type) => {
        setPaymentType(type);
        setFieldErrors({});
        setTouched({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Mark all relevant fields as touched so errors show
        const validators = paymentType === 'bank' ? BANK_VALIDATORS : UPI_VALIDATORS;
        const allTouched = Object.keys(validators).reduce((acc, k) => ({ ...acc, [k]: true }), {});
        setTouched(allTouched);

        const errors = validate(paymentType, formData);
        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            toast.error("Please fix the highlighted errors before submitting.");
            return;
        }

        setLoading(true);

        const payload = {
            code: couponCode.trim(),
            payment_type: paymentType,
            amount: couponDetails?.total_amount || 0,
            tax: couponDetails?.coupon_discount || 0,
            total: couponDetails?.total_after_discount || 0,
            ...formData,
        };

        if (paymentType === 'bank') {
            delete payload.gpay_upi_id;
            delete payload.gpay_phone_number;
        } else {
            delete payload.bank_name;
            delete payload.bank_account_number;
            delete payload.bank_ifsc_code;
            delete payload.bank_beneficiary_name;
            delete payload.bank_phone_number;
        }

        try {
            const res = await submit_enhancement(payload);
            if (res.status || res.result) {
                toast.success(res.message || "Enhancement submitted successfully");
                setIsValidated(false);
                setCouponCode('');
                setFormData(INITIAL_FORM);
                setFieldErrors({});
                setTouched({});
                fetchEncashmentList(user?.id);
            } else {
                toast.error(res.message || "Submission failed");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error submitting enhancement");
        } finally {
            setLoading(false);
        }
    };

    // Shared props factory for inputs
    const inputProps = (name) => ({
        name,
        value: formData[name],
        onChange: handleInputChange,
        onBlur: handleBlur,
        className: `form-control ${touched[name] ? (fieldErrors[name] ? 'is-invalid' : 'is-valid') : ''}`,
    });

    return (
        <div className="container py-4">
            <div className="card shadow-sm border-0 bg-white">
                <div className="card-body p-4">
                    <h4 className="card-title mb-4 border-bottom pb-2 fw-bold" style={{ color: '#82A240' }}>Encashment</h4>

                    {!isValidated ? (
                        <div className="row">
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Coupon Code</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter Coupon Code"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                    />
                                    <button className="btn" style={{ backgroundColor: '#82A240', color: '#fff' }} onClick={handleValidate} disabled={loading}>
                                        {loading ? "Checking..." : "Validate"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate>
                            {/* ── Coupon summary ── */}
                            <div className="alert bg-opacity-10 mb-4" style={{ backgroundColor: '#f1f8e9', border: '1px solid #82A240' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h5 className="alert-heading mb-0 fw-bold" style={{ color: '#82A240' }}>Coupon Details</h5>
                                    <button type="button" className="btn btn-sm" style={{ border: '1px solid #82A240', color: '#82A240' }} onClick={() => { setIsValidated(false); setFieldErrors({}); setTouched({}); }}>
                                        Change Code
                                    </button>
                                </div>
                                <hr style={{ backgroundColor: '#82A240' }} />
                                <div className="row text-dark">
                                    <div className="col-md-4">
                                        <p className="mb-1">Total Amount: <span className="fw-bold">₹{couponDetails?.total_amount}</span></p>
                                    </div>
                                    <div className="col-md-4">
                                        <p className="mb-1 text-danger">Service Charge: <span className="fw-bold">- ₹{couponDetails?.coupon_discount}</span></p>
                                    </div>
                                    <div className="col-md-4">
                                        <p className="mb-1" style={{ color: '#82A240' }}>Net Amount: <span className="fw-bold fs-5">₹{couponDetails?.total_after_discount}</span></p>
                                    </div>
                                </div>
                            </div>

                            {/* ── Payment method selector ── */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <label className="form-label fw-bold d-block mb-3">Select Encashment Method</label>
                                    <div className="d-flex gap-3">
                                        {[
                                            { key: 'bank', label: 'Bank Details' },
                                            { key: 'upi', label: 'Online Payment (UPI)' },
                                        ].map(({ key, label }) => (
                                            <div
                                                key={key}
                                                className={`p-3 border rounded flex-fill text-center`}
                                                onClick={() => handlePaymentTypeChange(key)}
                                                style={{ 
                                                    cursor: 'pointer', 
                                                    transition: '0.3s',
                                                    borderColor: paymentType === key ? '#82A240' : '#dee2e6',
                                                    backgroundColor: paymentType === key ? '#f1f8e9' : '#fff',
                                                    color: paymentType === key ? '#82A240' : 'inherit',
                                                    fontWeight: paymentType === key ? 'bold' : 'normal'
                                                }}
                                            >
                                                <h6 className="mb-0">{label}</h6>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* ── Payment fields ── */}
                            <div className="bg-light p-4 rounded border mb-4">
                                <h6 className="mb-4 fw-bold">
                                    {paymentType === 'bank' ? 'Enter Bank Information' : 'Enter UPI Information'}
                                </h6>
                                <div className="row g-3">
                                    {paymentType === 'bank' ? (
                                        <>
                                            <div className="col-md-6">
                                                <label className="form-label">Bank Name <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. HDFC Bank"
                                                    {...inputProps('bank_name')}
                                                />
                                                <FieldError msg={fieldErrors.bank_name} />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Account Number <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter your A/C number"
                                                    maxLength={18}
                                                    {...inputProps('bank_account_number')}
                                                />
                                                <FieldError msg={fieldErrors.bank_account_number} />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">IFSC Code <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. HDFC0001234"
                                                    maxLength={11}
                                                    {...inputProps('bank_ifsc_code')}
                                                    onChange={(e) => {
                                                        // Auto-uppercase IFSC
                                                        e.target.value = e.target.value.toUpperCase();
                                                        handleInputChange(e);
                                                    }}
                                                />
                                                <FieldError msg={fieldErrors.bank_ifsc_code} />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Beneficiary Name <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="Name as per bank records"
                                                    {...inputProps('bank_beneficiary_name')}
                                                />
                                                <FieldError msg={fieldErrors.bank_beneficiary_name} />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                                                <input
                                                    type="tel"
                                                    placeholder="Registered phone number"
                                                    maxLength={10}
                                                    {...inputProps('bank_phone_number')}
                                                />
                                                <FieldError msg={fieldErrors.bank_phone_number} />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="col-md-6">
                                                <label className="form-label">UPI ID <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. name@upi"
                                                    {...inputProps('gpay_upi_id')}
                                                />
                                                <FieldError msg={fieldErrors.gpay_upi_id} />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                                                <input
                                                    type="tel"
                                                    placeholder="Phone number linked to UPI"
                                                    maxLength={10}
                                                    {...inputProps('gpay_phone_number')}
                                                />
                                                <FieldError msg={fieldErrors.gpay_phone_number} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="text-end">
                                <button type="submit" className="btn btn-lg px-5 shadow-sm" style={{ backgroundColor: '#82A240', color: '#fff' }} disabled={loading}>
                                    {loading ? "Processing..." : "Submit for Encashment"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {encashmentNote && (
                <div className="alert alert-info border-0 mt-3 mb-0" style={{ borderLeft: '4px solid #0dcaf0', borderRadius: '8px' }}>
                    <strong>📝 Note:</strong> {encashmentNote}
                </div>
            )}

            {/* ── History table ── */}
            <div className="card shadow-sm border-0 bg-white mt-4">
                <div className="card-body">
                    <h5 className="card-title mb-3" style={{ color: '#82A240' }}>Encashment History</h5>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>Code</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Tax</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listLoading ? (
                                    <tr><td colSpan="7" className="text-center p-4">Loading history...</td></tr>
                                ) : encashmentList.length > 0 ? (
                                    encashmentList.map((item, index) => (
                                        <tr key={index}>
                                            <td className="fw-bold">{item.name}</td>
                                            <td className="text-capitalize">{item.payment_type}</td>
                                            <td>₹{item.amount}</td>
                                            <td>₹{item.gst}</td>
                                            <td className="fw-bold" style={{ color: '#82A240' }}>₹{item.total_amount}</td>
                                            <td>
                                                {item.status === 1 ? (
                                                    <span className="badge" style={{ backgroundColor: '#82A240', color: '#fff' }}>Approved</span>
                                                ) : item.status === 2 ? (
                                                    <span className="badge bg-danger">Rejected</span>
                                                ) : (
                                                    <span className="badge bg-warning text-dark">Pending</span>
                                                )}
                                            </td>
                                            <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="7" className="text-center p-4 text-muted">No encashment history found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Enchashment;